import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface FeatureFlag {
  key: string;
  enabled: boolean | null;
  rollout_percent: number | null;
  allowlist_user_ids: string[] | null;
  safe_default: boolean | null;
  description: string | null;
  category: string | null;
  requires_confirmation: boolean | null;
  created_at: string;
  updated_at: string;
}

interface UseAdminFeatureFlagsResult {
  flags: FeatureFlag[];
  loading: boolean;
  fetchFlags: () => Promise<void>;
  updateFlag: (key: string, enabled?: boolean, rolloutPercent?: number) => Promise<boolean>;
  addToAllowlist: (key: string, userId: string) => Promise<boolean>;
  removeFromAllowlist: (key: string, userId: string) => Promise<boolean>;
  resetToSafeDefault: (key: string) => Promise<boolean>;
}

export function useAdminFeatureFlags(): UseAdminFeatureFlagsResult {
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchFlags = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('*')
        .order('category', { ascending: true })
        .order('key', { ascending: true });

      if (error) {
        // Table might not exist - return empty array
        if (error.code === '42P01' || error.message?.includes('does not exist') || error.code === 'PGRST204') {
          console.warn('feature_flags table not found - returning empty array');
          setFlags([]);
          return;
        }
        throw error;
      }
      setFlags(data || []);
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      toast.error('Failed to fetch feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateFlag = useCallback(async (
    key: string,
    enabled?: boolean,
    rolloutPercent?: number
  ): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('admin_update_feature_flag', {
        p_flag_key: key,
        p_enabled: enabled ?? null,
        p_rollout_percent: rolloutPercent ?? null
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table update
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('admin_update_feature_flag RPC not found - falling back to direct table update');

          const updateData: { enabled?: boolean; rollout_percent?: number; updated_at: string } = {
            updated_at: new Date().toISOString()
          };

          if (enabled !== undefined) {
            updateData.enabled = enabled;
          }
          if (rolloutPercent !== undefined) {
            updateData.rollout_percent = rolloutPercent;
          }

          const { error: updateError } = await supabase
            .from('feature_flags')
            .update(updateData)
            .eq('key', key);

          if (updateError) throw updateError;

          toast.success('Feature flag updated');
          return true;
        }
        throw error;
      }

      const result = data as { success: boolean; message?: string; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to update feature flag');
      }

      toast.success(result.message || 'Feature flag updated');
      return true;
    } catch (err) {
      console.error('Failed to update feature flag:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to update feature flag');
      return false;
    }
  }, []);

  const addToAllowlist = useCallback(async (key: string, userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('admin_add_to_allowlist', {
        p_flag_key: key,
        p_user_id: userId
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table update
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('admin_add_to_allowlist RPC not found - falling back to direct table update');

          // Get current allowlist
          const { data: flagData, error: fetchError } = await supabase
            .from('feature_flags')
            .select('allowlist_user_ids')
            .eq('key', key)
            .single();

          if (fetchError) throw fetchError;

          const currentAllowlist = flagData?.allowlist_user_ids || [];
          if (!currentAllowlist.includes(userId)) {
            const { error: updateError } = await supabase
              .from('feature_flags')
              .update({
                allowlist_user_ids: [...currentAllowlist, userId],
                updated_at: new Date().toISOString()
              })
              .eq('key', key);

            if (updateError) throw updateError;
          }

          toast.success('User added to allowlist');
          return true;
        }
        throw error;
      }

      const result = data as { success: boolean; message?: string; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to add user to allowlist');
      }

      toast.success(result.message || 'User added to allowlist');
      return true;
    } catch (err) {
      console.error('Failed to add to allowlist:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to add to allowlist');
      return false;
    }
  }, []);

  const removeFromAllowlist = useCallback(async (key: string, userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('admin_remove_from_allowlist', {
        p_flag_key: key,
        p_user_id: userId
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table update
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('admin_remove_from_allowlist RPC not found - falling back to direct table update');

          // Get current allowlist
          const { data: flagData, error: fetchError } = await supabase
            .from('feature_flags')
            .select('allowlist_user_ids')
            .eq('key', key)
            .single();

          if (fetchError) throw fetchError;

          const currentAllowlist = flagData?.allowlist_user_ids || [];
          const updatedAllowlist = currentAllowlist.filter((id: string) => id !== userId);

          const { error: updateError } = await supabase
            .from('feature_flags')
            .update({
              allowlist_user_ids: updatedAllowlist,
              updated_at: new Date().toISOString()
            })
            .eq('key', key);

          if (updateError) throw updateError;

          toast.success('User removed from allowlist');
          return true;
        }
        throw error;
      }

      const result = data as { success: boolean; message?: string; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to remove user from allowlist');
      }

      toast.success(result.message || 'User removed from allowlist');
      return true;
    } catch (err) {
      console.error('Failed to remove from allowlist:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to remove from allowlist');
      return false;
    }
  }, []);

  const resetToSafeDefault = useCallback(async (key: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase.rpc('admin_reset_flag_to_safe_default', {
        p_flag_key: key
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table update
        if (error.code === '42883' || error.code === 'PGRST202' || (error as { status?: number }).status === 404) {
          console.warn('admin_reset_flag_to_safe_default RPC not found - falling back to direct table update');

          // Get safe_default value
          const { data: flagData, error: fetchError } = await supabase
            .from('feature_flags')
            .select('safe_default')
            .eq('key', key)
            .single();

          if (fetchError) throw fetchError;

          const safeDefault = flagData?.safe_default ?? false;

          const { error: updateError } = await supabase
            .from('feature_flags')
            .update({
              enabled: safeDefault,
              rollout_percent: 0,
              updated_at: new Date().toISOString()
            })
            .eq('key', key);

          if (updateError) throw updateError;

          toast.success('Feature flag reset to safe default');
          return true;
        }
        throw error;
      }

      const result = data as { success: boolean; message?: string; error?: string };

      if (!result.success) {
        throw new Error(result.error || 'Failed to reset feature flag');
      }

      toast.success(result.message || 'Feature flag reset to safe default');
      return true;
    } catch (err) {
      console.error('Failed to reset feature flag:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to reset feature flag');
      return false;
    }
  }, []);

  return {
    flags,
    loading,
    fetchFlags,
    updateFlag,
    addToAllowlist,
    removeFromAllowlist,
    resetToSafeDefault
  };
}
